import React, { useState } from 'react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import logoSvg from '../../assets/logo.svg';
import type { ApiResponse, OtpVerificationResponse } from '../../services';
import { validateEmail, isValidEmailFormat } from '../../utils/emailValidation';

interface LoginPageProps {
  onSendOtp: (email: string) => Promise<ApiResponse>;
  onVerifyOtp: (email: string, otp: string) => Promise<ApiResponse<OtpVerificationResponse>>;
  onResendOtp: (email: string) => Promise<ApiResponse>;
}

const LoginPage: React.FC<LoginPageProps> = ({ onSendOtp, onVerifyOtp, onResendOtp }) => {
  // Utility to clear login state from localStorage (call on logout)
  // Remove this function if not used in this file; use in your logout handler elsewhere.
  // Persist step state in localStorage to avoid reset on remount
  const [step, setStep] = useState<'email' | 'otp'>(() => {
    const saved = localStorage.getItem('login_step');
    return saved === 'otp' ? 'otp' : 'email';
  });

  const setStepPersist = (newStep: 'email' | 'otp') => {
    setStep(newStep);
    localStorage.setItem('login_step', newStep);
  };
  // Persist email state in localStorage to avoid reset on remount
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('login_email') || '';
  });

  const setEmailPersist = (newEmail: string) => {
    setEmail(newEmail);
    localStorage.setItem('login_email', newEmail);
  };
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailErrorType, setEmailErrorType] = useState<'validation' | 'server'>('validation'); // Track error type
  const [otpError, setOtpError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Resend OTP counter state
  const [resendCounter, setResendCounter] = useState(0);
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCounter > 0) {
      timer = setTimeout(() => {
        setResendCounter(resendCounter - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCounter]);

  const handleSendOtp = async () => {
    // Don't clear errors immediately - only clear them if validation passes
    setSuccessMessage('');
    
    // Validate email including domain check
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.error || 'Invalid email');
      setEmailErrorType('validation');
      return;
    }

    // Clear any previous errors when starting a new request (including server errors)
    setEmailError('');
    setIsLoading(true);
    
    try {
      const result = await onSendOtp(email);
      console.log('🔍 sendOtp response:', JSON.stringify(result, null, 2));
      if (result.status) {
        setStepPersist('otp');
        setSuccessMessage(result.message || 'OTP sent successfully!');
        setResendCounter(30); // Start counter when OTP sent successfully
        setEmailError(''); // Clear any previous errors on success
      } else {
        setEmailError(result.message || 'Failed to send OTP. Please try again.');
        setEmailErrorType('server'); // Mark as server error
        setSuccessMessage(''); // Clear success message on error
      }
    } catch (error) {
      console.error('Unexpected error in handleSendOtp:', error);
      setEmailError('Failed to send OTP. Please try again.');
      setEmailErrorType('server'); // Mark as server error
      setSuccessMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setOtpError('');

      // Auto-focus next input
      if (value && index < 3) {
        const nextInput = document.querySelector(`input[data-otp-index="${index + 1}"]`) as HTMLInputElement;
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace to move to previous input
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[data-otp-index="${index - 1}"]`) as HTMLInputElement;
      prevInput?.focus();
    }
    
    // Handle Enter key to verify OTP when all fields are filled
    if (e.key === 'Enter' && otp.join('').length === 4) {
      handleVerifyOtp();
    }
    
    // Handle arrow keys for navigation
    if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.querySelector(`input[data-otp-index="${index - 1}"]`) as HTMLInputElement;
      prevInput?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      const nextInput = document.querySelector(`input[data-otp-index="${index + 1}"]`) as HTMLInputElement;
      nextInput?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 4) {
      setOtpError('Please enter complete OTP');
      return;
    }

    setIsLoading(true);
    setOtpError('');
    setSuccessMessage('');
    
    const result = await onVerifyOtp(email, otpValue);
    
    if (result.status) {
      // Login successful - show success message briefly
      setSuccessMessage(result.message || 'Login successful! Redirecting...');
      // Clear any previous errors
      setOtpError('');
      // Keep loading state true during navigation to prevent any flickering
      // The container and router will handle the rest
    } else {
      // Show error from API, clear OTP fields, focus first input
      setOtpError(result.message || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '']);
      setIsLoading(false);
      // Focus first input after a short delay to ensure DOM is updated
      setTimeout(() => {
        const firstInput = document.querySelector(`input[data-otp-index="0"]`) as HTMLInputElement;
        firstInput?.focus();
      }, 100);
    }
  };

  const handleResendOtp = async () => {
    setOtp(['', '', '', '']);
    setSuccessMessage(''); // Clear success message
    setIsLoading(true);
    setResendCounter(30); // Start/restart counter on click
    
    try {
      const result = await onResendOtp(email);
      if (result.status) {
        setSuccessMessage('OTP resent successfully');
        setOtpError(''); // Clear any previous errors only on success
      } else {
        setOtpError(result.message || 'Failed to resend OTP. Please try again.');
        setSuccessMessage(''); // Clear success message if error occurs
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setOtpError('Failed to resend OTP. Please try again.');
      setSuccessMessage(''); // Clear success message if error occurs
    } finally {
      setIsLoading(false);
    }
  };

  return (
  <div className="min-h-screen-safe relative flex items-center justify-center bg-white from-primary-50 to-primary-100 px-4 py-6 pb-20 sm:p-6 sm:pb-20 lg:p-10">
       <div className="rounded-lg shadow-sm border w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl">
        {/* Logo and Header */}
      

        {/* Login Card */}
        <Card className="w-full p-6 sm:p-8 lg:p-10 xl:p-12">

           <div className="text-center">
          <div className="mx-auto w-32 sm:w-40 lg:w-48 h-auto mb-6 sm:mb-8 lg:mb-10">
            <img src={logoSvg} alt="Gentari Logo" className="w-full h-full" />
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary-700 mb-1">
            GENTARI PORTAL
          </h1>
          <p className="text-cyan-400 text-xs sm:text-sm lg:text-base mb-8 sm:mb-10 lg:mb-12">
            Your Employee EPM | 360 Feedback Portal
          </p>
        </div>
          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 sm:p-5 bg-green-100 border border-green-400 text-green-700 rounded-lg text-xs sm:text-sm animate-pulse">
              <div className="flex items-center justify-center space-x-2">
          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="font-medium">{successMessage}</span>
              </div>
            </div>
          )}

          {step === 'email' ? (
            <div className="space-y-6 sm:space-y-8">
          <div className="space-y-4 sm:space-y-5">
          <input
            type="email"
            value={email}
            onChange={(e) => {
            const newEmail = e.target.value;
            setEmailPersist(newEmail);
            
            // Clear any server errors when user starts typing a new email
            if (emailErrorType === 'server') {
              setEmailError('');
            }
            
            // Real-time email validation - only show validation errors after @ is typed
            if (newEmail && newEmail.includes('@') && newEmail.includes('.')) {
              const validation = validateEmail(newEmail);
              if (!validation.isValid) {
                setEmailError(validation.error || 'Invalid email');
                setEmailErrorType('validation');
              } else {
                // Clear validation errors when email becomes valid
                if (emailErrorType === 'validation') {
                  setEmailError('');
                }
              }
            } else if (newEmail && newEmail.includes('@') && !isValidEmailFormat(newEmail)) {
              setEmailError('Invalid email format');
              setEmailErrorType('validation');
            } else {
              // Only clear error if it's a validation error, not a server error
              if (emailErrorType === 'validation') {
                setEmailError('');
              }
            }
            }}
            placeholder="Enter your email"
            className={`w-full px-4 sm:px-5 py-3 sm:py-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
            emailError ? 'border-red-500' : 'border-gray-300'
            }`}
            onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSendOtp();
            }
            }}
          />
          {emailError && (
            <p className="text-red-500 text-xs sm:text-sm">{emailError}</p>
          )}
          </div>

              <Button
          onClick={handleSendOtp}
          disabled={isLoading || (emailError !== '' && emailErrorType === 'validation') }
          className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 sm:py-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base touch-target"
              >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
              Sending OTP...
            </>
          ) : (
            'Send OTP'
          )}
              </Button>
            </div>
          ) : (
            <div className="space-y-6 sm:space-y-8">
              {/* OTP Input */}
              <div className="space-y-4 sm:space-y-5">
          <div className="text-center text-xs sm:text-sm text-gray-600 mb-4 sm:mb-5">
            Enter the 4-digit OTP sent to <span className="font-semibold">{email}</span>
          </div>
          <div className="flex justify-center space-x-3 sm:space-x-4 md:space-x-5">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                data-otp-index={index}
                className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-center text-lg sm:text-xl font-semibold border-2 rounded-lg focus:outline-none transition-colors duration-200 touch-target ${
            otpError 
              ? 'border-red-500 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-300 focus:border-red-500' 
              : 'border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                }`}
                maxLength={1}
                autoComplete="off"
              />
            ))}
          </div>
          {otpError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center animate-pulse">
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-red-600 text-sm font-medium leading-tight">{otpError}</p>
              </div>
             
            </div>
          )}
              </div>

              {/* Verify Button */}
              <Button
          onClick={handleVerifyOtp}
          disabled={isLoading ||  otp.join('').length !== 4}
          className={`w-full py-3 sm:py-4 rounded-lg font-medium transition-all duration-200 ${
            otp.join('').length === 4 && !isLoading
              ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg transform hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
              >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Verifying...
            </>
          ) : otp.join('').length === 4 ? (
            <>
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Verify OTP
            </>
          ) : (
            'Verify OTP'
          )}
              </Button>

              {/* Resend OTP Button */}
              <Button
          onClick={handleResendOtp}
          disabled={isLoading || resendCounter > 0}
          variant="outline"
          className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 py-3 sm:py-4 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
          {isLoading
            ? 'Resending...'
            : resendCounter > 0
              ? `Resend OTP (${resendCounter}s)`
              : 'Resend OTP'}
              </Button>

              {/* Change Email Link */}
              <div className="text-center text-sm text-gray-600 pt-2">
          Not your email?{' '}
          <button 
            onClick={() => {
              setStepPersist('email');
              setOtp(['', '', '', '']);
              setOtpError('');
              setSuccessMessage('');
            }}
            className="text-primary-600 hover:text-primary-700 underline"
            disabled={isLoading}
          >
            Change email
          </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-10 sm:mt-12 pt-6 border-t border-gray-100 text-center text-sm text-gray-500">
          <p>© 2025 Gentari. All rights reserved.</p>
          <p className="mt-1">
            <a href="/privacy-policy" className="hover:text-primary-600">Privacy Policy</a>
            {' • '}
            <a href="/terms-conditions" className="hover:text-primary-600">Terms of Service</a>
          </p>
        </div>
        </Card>
        
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-sm text-gray-600">
          Made with <span className="text-red-500">♥</span> by Gentari India Digital
        </p>
      </div>
    </div>
    
  );
};

export default LoginPage;
